import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

const SEEN_KEY = 'vaanexa-intro-seen';
const EASE = [0.16, 1, 0.3, 1] as const;
/** How long the brand panel holds before it lifts away to reveal the site. */
const HOLD_MS = 2100;

function alreadySeen(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* private mode — just show it again next visit, no harm */
  }
}

/**
 * A once-per-session brand curtain that plays over the freshly-painted site:
 * the logo blooms in, the wordmark rises, a progress sweep fills, then the whole
 * dark panel lifts up to reveal the (light) hero beneath.
 *
 * It renders ON TOP of the real page — the site paints behind it for LCP/SEO —
 * and is skipped entirely under reduced-motion or on repeat navigations.
 */
export function SiteIntro({
  brandName,
  logoUrl,
}: {
  brandName: string;
  logoUrl: string;
}): JSX.Element | null {
  const reduce = useReducedMotion();
  const [show, setShow] = useState(() => !reduce && !alreadySeen());
  const [pct, setPct] = useState(0);
  const raf = useRef(0);

  useEffect(() => {
    if (!show) return;
    markSeen();

    // Lock scroll while the curtain is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Drive the visible progress counter to 100% just before the lift.
    const start = performance.now();
    const fillMs = HOLD_MS - 350;
    const tick = (now: number): void => {
      const p = Math.min(1, (now - start) / fillMs);
      setPct(Math.round(p * 100));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);

    const timer = window.setTimeout(() => setShow(false), HOLD_MS);
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf.current);
      document.body.style.overflow = prevOverflow;
    };
  }, [show]);

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          key="intro"
          className="fixed inset-0 z-[100] grid place-items-center overflow-hidden"
          style={{
            background:
              'radial-gradient(70% 55% at 50% 32%, rgba(122,59,255,0.28) 0%, transparent 60%), linear-gradient(165deg, #140a30 0%, #070312 60%)',
          }}
          initial={{ opacity: 1 }}
          exit={{ y: '-100%' }}
          transition={{ duration: 0.85, ease: EASE }}
          aria-hidden
        >
          {/* Soft aurora bloom drifting behind the mark. */}
          <motion.div
            className="pointer-events-none absolute h-[42rem] w-[42rem] rounded-full blur-[120px]"
            style={{
              background:
                'conic-gradient(from 200deg, rgba(122,59,255,0.5), rgba(255,199,90,0.2), rgba(80,40,200,0.45), rgba(122,59,255,0.5))',
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.5, scale: 1, rotate: 18 }}
            transition={{ duration: 2.2, ease: 'easeInOut' }}
          />

          <div className="relative flex flex-col items-center gap-6">
            {/* Logo bloom. */}
            <motion.div
              className="relative grid h-24 w-24 place-items-center"
              initial={{ opacity: 0, scale: 0.55, filter: 'blur(8px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.75, ease: EASE, delay: 0.1 }}
            >
              <img
                src={logoUrl}
                alt=""
                width={96}
                height={96}
                className="relative h-full w-full object-contain brightness-0 invert drop-shadow-[0_0_24px_rgba(246,243,255,0.35)]"
              />
            </motion.div>

            {/* Wordmark. */}
            <motion.p
              className="font-[var(--font-display)] text-3xl font-semibold tracking-tight text-[#f6f3ff] sm:text-4xl"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.45 }}
            >
              {brandName}
            </motion.p>

            {/* Progress sweep + counter. */}
            <motion.div
              className="flex w-56 flex-col items-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: EASE, delay: 0.7 }}
            >
              <div className="h-[3px] w-full overflow-hidden rounded-full bg-white/12">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: 'linear-gradient(90deg, #7a3bff 0%, #c3a0ff 55%, #ffc75a 100%)',
                  }}
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: (HOLD_MS - 350) / 1000, ease: 'easeInOut' }}
                />
              </div>
              <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#8f88b2]">
                {pct}%
              </span>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
